// Shared color utilities for prayer backgrounds.
//
// When the author picks a solid color as their "no image" background we
// render it as a soft top→bottom gradient instead of a flat block. The
// gradient goes from a darker shade of the base color at the top to a
// lighter tint at the bottom, which reads as more visually alive while
// still respecting the author's choice.
//
// Also exports a WCAG-luminance-based readable-text picker so title text
// stays legible whether the base color is dark or light.

const DEFAULT_GRADIENT =
  'linear-gradient(to bottom, #2e1065 0%, #5b21b6 55%, #c4b5fd 100%)';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function hexToRgb(hex) {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function rgbToHex(r, g, b) {
  const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${clamp(r).toString(16).padStart(2, '0')}${clamp(g).toString(16).padStart(2, '0')}${clamp(b).toString(16).padStart(2, '0')}`;
}

// Move an RGB channel toward black (t<0) or white (t>0). t in [-1, 1].
function shade([r, g, b], t) {
  if (t < 0) {
    const k = 1 + t; // 0..1
    return [r * k, g * k, b * k];
  }
  return [r + (255 - r) * t, g + (255 - g) * t, b + (255 - b) * t];
}

/**
 * Given a base hex color, return a CSS `background` value that fades from a
 * darker version at the top to a lighter version at the bottom. Falls back
 * to the app's default purple gradient when the input isn't a valid hex.
 */
export function bgGradient(hex) {
  if (!hex || !HEX_RE.test(hex)) return DEFAULT_GRADIENT;
  const base = hexToRgb(hex);
  const dark = rgbToHex(...shade(base, -0.32));
  const light = rgbToHex(...shade(base, 0.42));
  return `linear-gradient(to bottom, ${dark} 0%, ${hex} 55%, ${light} 100%)`;
}

/**
 * Return a text color (#0f172a near-black, or #ffffff white) that stays
 * readable against the given base hex. Uses relative luminance per WCAG.
 * Returns white when the input isn't a valid hex.
 */
export function readableTextOn(hex) {
  if (!hex || !HEX_RE.test(hex)) return '#ffffff';
  const [r, g, b] = hexToRgb(hex).map((c) => c / 255);
  const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return y > 0.6 ? '#0f172a' : '#ffffff';
}

/** Convenience: shadow that stays legible on both light and dark bg. */
export function readableTextShadow(hex) {
  return readableTextOn(hex) === '#ffffff'
    ? '0 2px 12px rgba(0,0,0,0.55)'
    : '0 1px 3px rgba(255,255,255,0.6)';
}
